import { useUser, useClerk } from '@clerk/clerk-react';
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/api/supabase';
import { siteConfig } from '@/config/site.config';
import { clearCart } from '@/lib/utils/cartPersistence';
import type {
  EventRegistration,
  TournamentRegistration,
  ActivityRegistration,
  Order,
  UserRegistrations,
} from '@/types';

interface Profile {
  id: string;
  clerkUserId: string;
  eventId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  createdAt: string;
}

export function useAuth() {
  const { isSignedIn, user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  // Sync user profile with Supabase
  const syncProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setIsProfileLoading(false);
      return;
    }

    try {
      setIsProfileLoading(true);

      // Check for existing registration
      const { data: existingReg } = await supabase
        .from('event_registrations')
        .select('*')
        .eq('clerk_user_id', user.id)
        .eq('event_id', siteConfig.eventId)
        .single();

      if (existingReg) {
        setProfile({
          id: existingReg.id,
          clerkUserId: existingReg.clerk_user_id,
          eventId: existingReg.event_id,
          email: existingReg.user_email,
          firstName: existingReg.user_name?.split(' ')[0],
          lastName: existingReg.user_name?.split(' ').slice(1).join(' '),
          createdAt: existingReg.created_at,
        });
      } else {
        // Just set a basic profile from Clerk data
        setProfile({
          id: '',
          clerkUserId: user.id,
          eventId: siteConfig.eventId,
          email: user.primaryEmailAddress?.emailAddress || '',
          firstName: user.firstName ?? undefined,
          lastName: user.lastName ?? undefined,
          createdAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Error syncing profile:', error);
    } finally {
      setIsProfileLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isLoaded) {
      syncProfile();
    }
  }, [isLoaded, syncProfile]);

  const handleSignOut = useCallback(async () => {
    // Clear cart data on sign out for security
    if (user?.id) {
      clearCart(user.id);
    }
    clearCart(); // Also clear anonymous cart
    await signOut();
    setProfile(null);
  }, [signOut, user?.id]);

  return {
    isSignedIn: !!isSignedIn,
    isLoading: !isLoaded || (isSignedIn && isProfileLoading),
    user,
    profile,
    signOut: handleSignOut,
    refreshProfile: syncProfile,
  };
}

/**
 * Hook to get the user's registrations for this event
 */
export function useUserRegistrations() {
  const { user, isSignedIn } = useAuth();
  const [registrations, setRegistrations] = useState<UserRegistrations>({
    eventRegistration: null,
    tournamentRegistrations: [],
    activityRegistrations: [],
    orders: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Track if the effect is still active to prevent state updates after unmount
    let isMounted = true;

    if (!isSignedIn || !user?.id) {
      setIsLoading(false);
      return;
    }

    const fetchRegistrations = async () => {
      try {
        setIsLoading(true);

        const [eventReg, tournamentRegs, activityRegs, ordersResult] = await Promise.all([
          supabase
            .from('event_registrations')
            .select('*')
            .eq('clerk_user_id', user.id)
            .eq('event_id', siteConfig.eventId)
            .single(),
          supabase
            .from('tournament_registrations')
            .select('*, tournaments(*)')
            .eq('clerk_user_id', user.id)
            .eq('event_id', siteConfig.eventId)
            .eq('payment_status', 'completed'),
          supabase
            .from('activity_registrations')
            .select('*, activities(*)')
            .eq('clerk_user_id', user.id)
            .eq('event_id', siteConfig.eventId)
            .eq('payment_status', 'completed'),
          supabase
            .from('orders')
            .select('*, order_items(*)')
            .eq('clerk_user_id', user.id)
            .eq('event_id', siteConfig.eventId)
            .eq('payment_status', 'completed')
            .order('created_at', { ascending: false }),
        ]);

        // Only update state if component is still mounted
        if (isMounted) {
          setRegistrations({
            eventRegistration: eventReg.data as EventRegistration | null,
            tournamentRegistrations: (tournamentRegs.data || []) as TournamentRegistration[],
            activityRegistrations: (activityRegs.data || []) as ActivityRegistration[],
            orders: (ordersResult.data || []) as Order[],
          });
        }
      } catch (error) {
        console.error('Error fetching registrations:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchRegistrations();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [isSignedIn, user?.id]);

  return {
    ...registrations,
    isLoading,
    hasEventRegistration: !!registrations.eventRegistration,
    registeredTournamentIds: registrations.tournamentRegistrations.map((r) => r.tournament_id),
  };
}
